import { Alert, AlertDescription } from '@/components/ui/alert';
import { trpc } from 'src/trpc';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useMemo, useState } from 'react';
import { ErrorMessage } from '@hookform/error-message';
import { Loader2 } from 'lucide-react';
import {
  MoneyFromDollars,
  Money,
  Discounts,
  Discount,
  DiscountType,
  safeMoneyFromCents,
} from '@libs/services/money';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableFooter,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Form } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '@/lib/utils';
import { useToast } from 'src/components/useToast';
import { NumericFormat } from 'react-number-format';
import type { Cart, Order } from 'src/trpc';
import { useOrderCurrentPricing } from 'components/orders/Details/useOrderCurrentPricing';

const DiscountPreview = ({
  existingDiscounts,
  newDiscount,
  showTotalTable,
  total,
  removeDiscount,
  authoritativeTotal,
  authoritativeDiscountBase,
  authoritativePricingState,
}: {
  existingDiscounts: DiscountType[];
  showTotalTable: boolean;
  newDiscount?: DiscountType;
  total: Money;
  removeDiscount: (idx: number) => void;
  authoritativeTotal?: Money;
  authoritativeDiscountBase?: Money;
  authoritativePricingState?: 'loading' | 'error' | 'ready';
}) => {
  const totalWithDiscounts = Discounts([
    ...existingDiscounts,
    ...(newDiscount ? [newDiscount] : []),
  ]);
  const renderExistingDiscounts = () => {
    return existingDiscounts.map((discount, index) => (
      <TableRow>
        <TableCell className="w-1/2">
          {discount.humanReadable()}
          <Button onClick={() => removeDiscount(index)} variant="link" className="text-red-400">
            Remove
          </Button>
        </TableCell>
      </TableRow>
    ));
  };
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-1/2">Discounts</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {renderExistingDiscounts()}
        {newDiscount ? (
          <TableRow>
            <TableCell className="w-1/2">{newDiscount.humanReadable()}</TableCell>
            {showTotalTable ? (
              <TableCell colSpan={3} className="text-right">
                -{newDiscount.valueFor(authoritativeDiscountBase || total).toFormat()}
              </TableCell>
            ) : null}
          </TableRow>
        ) : null}
        {existingDiscounts.length === 0 && !newDiscount ? (
          <TableCell colSpan={3}>No discounts</TableCell>
        ) : null}
      </TableBody>
      {showTotalTable ? (
        <TableFooter>
          <TableRow>
            <TableCell className="font-bold" colSpan={3}>
              Total
            </TableCell>
            <TableCell className="text-right font-bold">
              {authoritativePricingState === 'loading'
                ? 'Calculating...'
                : authoritativePricingState === 'error'
                  ? 'Unavailable'
                  : authoritativeTotal?.toFormat() || totalWithDiscounts.applyTo(total).toFormat()}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={4}>
              Note: The total here may include adjustments based on order status. Taxes may be
              included. Negative amounts indicate a refund is due.
            </TableCell>
          </TableRow>
        </TableFooter>
      ) : null}
    </Table>
  );
};

interface DiscountDialogProps {
  open: boolean;
  onClose: () => void;
  orderTotal: Money;
  orderId: string;
  showTotalTable: boolean;
  discounts: DiscountType[];
  pricingContext?: {
    order: Order;
    cart: Cart;
  };
}

export const DiscountDialog = ({
  open,
  onClose,
  orderTotal,
  orderId,
  showTotalTable,
  discounts,
  pricingContext,
}: DiscountDialogProps) => {
  const [error, setError] = useState<null | string>(null);
  const [draftDiscounts, setDraftDiscounts] = useState(discounts);
  const utils = trpc.useUtils();
  const { mutateAsync, isPending: applyDiscountLoading } =
    trpc.admin.order.adminApplyDiscountsToOrder.useMutation({
      onSuccess: () => {
        utils.admin.order.adminGetOrderRelatedInfoById.invalidate(orderId!);
        setError(null);
      },
      onError() {
        setError('Unable to apply discount to order. Please try again.');
      },
    });

  const { successToast } = useToast();
  const form = useForm({
    resolver: zodResolver(
      z
        .object({
          type: z.literal('percent'),
          amount: z.coerce.number().min(0),
        })
        .or(
          z.object({
            type: z.literal('dollars'),
            amount: z.coerce.number().min(0),
          }),
        ),
    ),
    mode: 'onSubmit', // Only validate on submit to avoid triggering validation on mount
    values: {
      type: 'percent' as 'percent' | 'dollars',
      amount: 0,
    },
  });

  // Watch form fields to enable/disable button and preview without triggering validation
  const amountValue = form.watch('amount');
  const typeValue = form.watch('type');
  const isFormReady = amountValue && Number(amountValue) > 0;
  const newDiscount = useMemo(
    () =>
      isFormReady
        ? Discount({
            amount:
              typeValue === 'percent'
                ? Number(amountValue)
                : MoneyFromDollars(Number(amountValue)).getAmount(),
            type: typeValue,
          })
        : undefined,
    [amountValue, isFormReady, typeValue],
  );
  const previewDiscounts = useMemo(
    () => [...draftDiscounts, ...(newDiscount ? [newDiscount] : [])],
    [draftDiscounts, newDiscount],
  );
  const previewPricing = useOrderCurrentPricing(
    pricingContext?.order,
    pricingContext?.cart,
    pricingContext ? previewDiscounts : undefined,
  );
  const authoritativePricingState = !pricingContext
    ? undefined
    : previewPricing.isTaxLoading
      ? 'loading'
      : previewPricing.isTaxError || previewPricing.totalInCents === undefined
        ? 'error'
        : 'ready';

  const removeDiscount = async (indexToRemove: number) => {
    const removedDiscount = draftDiscounts[indexToRemove];
    const discounts = draftDiscounts.filter((_, index) => index !== indexToRemove);
    setDraftDiscounts(discounts);

    // Filter out any invalid discounts before sending to backend
    const validDiscounts = discounts
      .map((d) => ({ amount: d.getAmount(), type: d.getType() }))
      .filter((d) => d.amount > 0 && (d.type === 'percent' || d.type === 'dollars'));

    await mutateAsync({
      orderId,
      discounts: validDiscounts,
    });
    successToast({ description: `Removed ${removedDiscount.humanReadable()} discount` });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        onClose();
        form.reset();
        setError(null);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Apply Discounts</DialogTitle>
          <DialogDescription>
            Apply discounts to the order. Discounts can exceed the order total, resulting in a
            refund.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <div className="flex gap-1">
            <ToggleGroup
              variant="outline"
              type="single"
              onValueChange={(val) => {
                form.setValue('type', val as 'percent' | 'dollars');
              }}
              value={typeValue}
              {...form.register('type')}
            >
              <ToggleGroupItem size="lg" value="percent">
                %
              </ToggleGroupItem>
              <ToggleGroupItem size="lg" value="dollars">
                $
              </ToggleGroupItem>
            </ToggleGroup>
            <NumericFormat
              value={amountValue}
              onValueChange={(values) => {
                form.setValue('amount', values.floatValue || 0);
              }}
              decimalScale={2}
              fixedDecimalScale={true}
              prefix={typeValue === 'dollars' ? '$' : ''}
              customInput={Input}
              className={form.formState.errors.amount ? 'border-2 border-rose-500' : ''}
              placeholder={typeValue === 'dollars' ? '$0.00' : '0%'}
            />
          </div>
          <span className="text-red-400">
            <ErrorMessage errors={form.formState.errors} name="amount" />
          </span>
          <DiscountPreview
            total={orderTotal}
            removeDiscount={removeDiscount}
            existingDiscounts={draftDiscounts}
            showTotalTable={showTotalTable}
            newDiscount={newDiscount}
            authoritativeTotal={
              !pricingContext || previewPricing.totalInCents === undefined
                ? undefined
                : safeMoneyFromCents(previewPricing.totalInCents)
            }
            authoritativeDiscountBase={
              pricingContext ? safeMoneyFromCents(previewPricing.subtotalInCents) : undefined
            }
            authoritativePricingState={authoritativePricingState}
          />
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Button
            onClick={form.handleSubmit((data) => {
              const newDiscount = {
                type: data.type,
                amount:
                  data.type === 'dollars'
                    ? MoneyFromDollars(Number(data.amount)).getAmount()
                    : Number(data.amount),
              };

              // Filter out any invalid discounts before sending to backend
              const validDiscounts = [...draftDiscounts.map((d) => d.toJSON()), newDiscount].filter(
                (d) => d.amount > 0 && (d.type === 'percent' || d.type === 'dollars'),
              );

              mutateAsync({
                orderId,
                discounts: validDiscounts,
              }).then(() => {
                successToast({
                  description: `Applied ${Discount(newDiscount).humanReadable()} discount successfully`,
                });
                form.reset();
                onClose();
                setDraftDiscounts(() => [...draftDiscounts, Discount(newDiscount)]);
              });
            })}
            disabled={!isFormReady}
          >
            {applyDiscountLoading ? (
              <Loader2 className={cn('animate-spin text-white', `h-4`, `w-4`)} />
            ) : (
              'Save'
            )}
          </Button>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
